import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { styled, useTheme } from '@mui/material/styles'

import {
  Box,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  Toolbar,
  Typography,
  AppBar as MuiAppBar
} from '@mui/material'

import {
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material'

import StaticFlash from './static/StaticFlash'
import NoPageMenu from './NoPageMenu'
import LoadingSpinner from './LoadingSpinner'
import PageMenu from '../redux/features/pages/PageMenu'
import PagesPane from '../redux/features/pages/PagesPane'
import StikisField from '../redux/features/stikis/StikisField'
import BottomPane from '../redux/features/stikis/BottomPane'

import { selectPage, selectPageStikisLoaded } from '../redux/features/pages/pagesSlice'
import {
  selectPagesPaneIsVisible,
  showPagesPane,
  hidePagesPane,
} from '../redux/features/ui/uiSlice'

const drawerWidth = 240

const classes = {
  background: {
    position: 'fixed',
    inset: 0,
    margin: 0,
    padding: 0,
    overflow: 'hidden',
    background: (theme) => theme.palette.grey[800],
    // TODO - to take account of top menu, set top: 74px,
    '&:focus': {
      outline: 'none',
    },
  },
  loading: {
    position: 'fixed',
    inset: 0,
    margin: 0,
    padding: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: (theme) => theme.palette.grey[800],
  },
  pageMissing: {
    color: (theme) => theme.palette.grey[300],
  },
}

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    flexGrow: 1,
    padding: theme.spacing(3),
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: `-${drawerWidth}px`,
    ...(open && {
      transition: theme.transitions.create('margin', {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
      marginLeft: 0,
    }),
  })
)

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: `${drawerWidth}px`,
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}))

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
  justifyContent: 'flex-end',
}))

export default function TopMenu({ pageId }) {
  const theme = useTheme()
  const page = useSelector(selectPage(pageId))
  const stikisLoaded = useSelector(selectPageStikisLoaded(pageId))
  // Whether the pages drawer is open lives in the store, not here, so it
  // outlasts this component: moving between routes (deleting a page goes
  // through /app/pages) remounts it, and an open drawer would otherwise snap
  // shut and open again.
  const dispatch = useDispatch()
  const open = useSelector(selectPagesPaneIsVisible)

  // It opens when there is no page to show (no pages at all, after deleting
  // the last one), or when the visit came by way of /app/pages, which asks
  // for the list (LastPage passes `openPagesPane`). After the first render,
  // so a drawer that was closed slides open rather than appearing; one
  // already open stays as it is.
  const location = useLocation()
  const shouldOpen = !Boolean(pageId) || Boolean(location.state?.openPagesPane)
  useEffect(() => {
    if (shouldOpen) {
      dispatch(showPagesPane())
    }
  }, [shouldOpen, location.key, dispatch])

  // Done trying, one way or another: no specific page was asked for, or its
  // stikis fetch has settled (loadStikisForPage, or fetchPageStikisAsync's
  // own failure path). Until then, show the spinner regardless of whether
  // the page's own metadata (a separate fetch, /pages.json) has arrived
  // yet -- waiting on just one of the two often left too short a window to
  // actually see it.
  const pageAttempted = !Boolean(pageId) || stikisLoaded

  const handleMenuIconClick = () => {
    dispatch(showPagesPane())
  }

  const handleDrawerClose = () => {
    dispatch(hidePagesPane())
  }

  return (
    <React.Fragment>
      <Box sx={{ display: 'flex' }}>
        <CssBaseline />
        <AppBar position='fixed' open={open}>
          <StaticFlash />
          <Toolbar>
            <IconButton
              color='inherit'
              aria-label='open drawer'
              onClick={handleMenuIconClick}
              edge='start'
              sx={{ mr: 2, ...(open && { display: 'none' }) }}
            >
              <MenuIcon />
            </IconButton>
            {Boolean(page) ? <PageMenu pageId={pageId} /> : <NoPageMenu />}
          </Toolbar>
        </AppBar>
        <Drawer
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              backgroundColor: (theme) => theme.palette.grey[200],
            },
          }}
          variant='persistent'
          anchor='left'
          open={open}
        >
          <DrawerHeader>
            <IconButton onClick={handleDrawerClose}>
              {theme.direction === 'ltr' ? (
                <ChevronLeftIcon />
              ) : (
                <ChevronRightIcon />
              )}
            </IconButton>
          </DrawerHeader>
          <Divider />
          <PagesPane currentPageId={pageId} />
        </Drawer>
        <Main open={open}>
          <DrawerHeader />
          {!pageAttempted ? (
            <Box sx={classes.loading}>
              <LoadingSpinner />
            </Box>
          ) : Boolean(page) ? (
            <StikisField pageId={pageId} />
          ) : Boolean(pageId) ? (
            // A page was asked for, the fetch is over, and there is no page:
            // it isn't mine or shared with me (401), or it doesn't exist
            // (404). The same words for both, so a stranger can't tell which
            // pages exist.
            <Box sx={classes.loading}>
              <Typography sx={classes.pageMissing}>
                Either that page does not exist, or you don't have access.
              </Typography>
            </Box>
          ) : (
            <Box sx={classes.background} className='background'></Box>
          )}
        </Main>
      </Box>
      {Boolean(page) && (
        <BottomPane pageId={pageId} pagesPaneIsVisible={open} />
      )}
    </React.Fragment>
  )
}

